import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

const PerformanceTrends = ({ allPlays, innings, awayAbbr, homeAbbr }) => {
  const { cumulativeData, pitchingPressure } = useMemo(() => {
    if (!allPlays?.length) return { cumulativeData: [], pitchingPressure: [] };

    let awayTotal = 0;
    let homeTotal = 0;
    const cumulative = [];
    const pressure = [];
    const inningMap = {};

    allPlays.forEach((play) => {
      const inning = play.about?.inning || 0;
      const rbi = play.result?.rbi || 0;
      const isTop = play.about?.isTopInning;
      const k = play.result?.event === 'Strikeout' ? 1 : 0;
      const bb = play.result?.event === 'Walk' ? 1 : 0;
      const hr = play.result?.event === 'Home Run' ? 1 : 0;

      if (!inningMap[inning]) inningMap[inning] = { k: 0, bb: 0, hr: 0, rbi: 0, inning };
      inningMap[inning].k += k;
      inningMap[inning].bb += bb;
      inningMap[inning].hr += hr;
      inningMap[inning].rbi += rbi;

      if (rbi > 0) {
        if (isTop) awayTotal += rbi;
        else homeTotal += rbi;
        cumulative.push({ inning, awayTotal, homeTotal });
      }
    });

    Object.values(inningMap)
      .sort((a, b) => a.inning - b.inning)
      .forEach((d) => pressure.push(d));

    return { cumulativeData: cumulative, pitchingPressure: pressure };
  }, [allPlays]);

  const cumulativeOption = {
    backgroundColor: 'transparent',
    grid: { top: 24, right: 20, bottom: 40, left: 44 },
    legend: { bottom: 0, textStyle: { color: '#a9c9b1', fontSize: 10 }, itemWidth: 12, itemHeight: 8 },
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#1e3a29',
      borderColor: '#658d71',
      textStyle: { color: '#d8ceb3', fontSize: 11 },
    },
    xAxis: {
      type: 'category',
      data: cumulativeData.map((_, i) => i + 1),
      axisLabel: { show: false },
      axisLine: { lineStyle: { color: '#658d71' } },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: '#a9c9b1', fontSize: 10 },
      axisLine: { lineStyle: { color: '#658d71' } },
      splitLine: { lineStyle: { color: 'rgba(101,141,113,0.2)' } },
    },
    series: [
      {
        name: awayAbbr,
        type: 'line',
        data: cumulativeData.map((d) => d.awayTotal),
        smooth: true,
        lineStyle: { color: '#a9c9b1', width: 2 },
        itemStyle: { color: '#a9c9b1' },
        symbol: 'none',
        areaStyle: { color: 'rgba(169,201,177,0.1)' },
      },
      {
        name: homeAbbr,
        type: 'line',
        data: cumulativeData.map((d) => d.homeTotal),
        smooth: true,
        lineStyle: { color: '#c4333e', width: 2 },
        itemStyle: { color: '#c4333e' },
        symbol: 'none',
        areaStyle: { color: 'rgba(196,51,62,0.1)' },
      },
    ],
  };

  const pressureOption = {
    backgroundColor: 'transparent',
    grid: { top: 24, right: 20, bottom: 40, left: 44 },
    legend: { bottom: 0, textStyle: { color: '#a9c9b1', fontSize: 10 }, itemWidth: 12, itemHeight: 8 },
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#1e3a29',
      borderColor: '#658d71',
      textStyle: { color: '#d8ceb3', fontSize: 11 },
    },
    xAxis: {
      type: 'category',
      data: pitchingPressure.map((d) => `Inn ${d.inning}`),
      axisLabel: { color: '#a9c9b1', fontSize: 9 },
      axisLine: { lineStyle: { color: '#658d71' } },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: '#a9c9b1', fontSize: 10 },
      axisLine: { lineStyle: { color: '#658d71' } },
      splitLine: { lineStyle: { color: 'rgba(101,141,113,0.2)' } },
    },
    series: [
      {
        name: 'Strikeouts',
        type: 'bar',
        data: pitchingPressure.map((d) => d.k),
        itemStyle: { color: 'rgba(169,201,177,0.8)', borderColor: '#a9c9b1', borderWidth: 1 },
        barGap: '10%',
      },
      {
        name: 'Walks',
        type: 'bar',
        data: pitchingPressure.map((d) => d.bb),
        itemStyle: { color: 'rgba(216,206,179,0.6)', borderColor: '#d8ceb3', borderWidth: 1 },
      },
      {
        name: 'Home Runs',
        type: 'bar',
        data: pitchingPressure.map((d) => d.hr),
        itemStyle: { color: 'rgba(196,51,62,0.8)', borderColor: '#c4333e', borderWidth: 1 },
      },
    ],
  };

  if (!cumulativeData.length && !pitchingPressure.length) {
    return (
      <div className="flex items-center justify-center h-40 text-[#658d71] text-xs tracking-widest uppercase">
        No trend data available
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Cumulative Run Scoring</span>
          <p className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest mt-0.5">
            Total runs accumulated over game progression
          </p>
        </div>
        <div className="p-2">
          <ReactECharts option={cumulativeOption} style={{ height: '180px' }} />
        </div>
      </div>

      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Pitching Pressure by Inning</span>
          <p className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest mt-0.5">
            Strikeouts, walks & home runs allowed per inning
          </p>
        </div>
        <div className="p-2">
          <ReactECharts option={pressureOption} style={{ height: '180px' }} />
        </div>
      </div>
    </div>
  );
};

export default PerformanceTrends;